import { useCallback, useEffect, useRef, useState } from "react";
import { Swords, Loader2, Check, X, Trophy, Clock } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { UserAvatar } from "@/components/UserBits";
import { successPop, confetti, playFanfare, playLose } from "@/lib/celebrate";
import { DuelVersus } from "./DuelVersus";
import { useGames } from "@/lib/games";

const errMsg = (e) => e?.response?.data?.detail || "Das hat nicht geklappt";
const left = (d) => {
  const ms = new Date(d) - Date.now();
  if (ms <= 0) return "endet gleich";
  const h = Math.floor(ms / 3600000);
  return h >= 24 ? `noch ${Math.floor(h / 24)} T ${h % 24} Std.` : `noch ${h} Std. ${Math.floor((ms % 3600000) / 60000)} Min.`;
};

function NewDuel({ onCreated, onVersus }) {
  const { user, refreshUser } = useAuth();
  const [friends, setFriends] = useState([]);
  const [opp, setOpp] = useState("");
  const [stake, setStake] = useState(20);
  const [busy, setBusy] = useState(false);
  const dc = useGames()?.duels || { min_stake: 5, max_stake: 500 };
  useEffect(() => { api.get(`/users/${user.user_id}/friends`).then(({ data }) => setFriends(data)).catch(() => {}); }, [user.user_id]);
  const send = async (e) => {
    setBusy(true);
    const btn = e.currentTarget;
    try {
      await api.post("/duels", { opponent_id: opp, stake: Number(stake) }); successPop(btn); toast.success("Herausforderung gesendet ⚔️");
      const f = friends.find((x) => x.user_id === opp);
      onVersus({ players: [user, f], title: "Herausforderung gesendet", text: `Du forderst ${f?.name} heraus – Einsatz ${stake} Punkte. Jetzt muss ${f?.name?.split(" ")[0]} nur noch annehmen!`, cta: "Los geht's! ⚔️" });
      setOpp(""); refreshUser(); onCreated();
    }
    catch (er) { toast.error(errMsg(er)); } finally { setBusy(false); }
  };
  if (!friends.length) return <p className="text-sm text-slate-500 rounded-2xl bg-rose-50 p-4" data-testid="duel-no-friends">Füge zuerst Freunde hinzu, um sie zum Duell herauszufordern.</p>;
  return (
    <div className="rounded-2xl bg-rose-50/60 p-4 space-y-3" data-testid="duel-new">
      <select value={opp} onChange={(e) => setOpp(e.target.value)} data-testid="duel-friend-select" className="w-full h-12 rounded-xl border border-pink-200 bg-white px-3 font-semibold">
        <option value="">Freund auswählen …</option>
        {friends.map((f) => <option key={f.user_id} value={f.user_id}>{f.name}</option>)}
      </select>
      <div className="flex items-center gap-2">
        {[10, 20, 50, 100].map((v) => <button key={v} type="button" onClick={() => setStake(v)} data-testid={`duel-stake-${v}`}
          className={`flex-1 h-11 rounded-xl font-extrabold text-sm transition-colors ${Number(stake) === v ? "pink-gradient text-white" : "bg-white border border-pink-200 text-rose-600"}`}>{v}</button>)}
        <input type="number" min={dc.min_stake} max={dc.max_stake} value={stake} onChange={(e) => setStake(e.target.value)} data-testid="duel-stake-input" className="w-20 h-11 rounded-xl border border-pink-200 bg-white px-2 text-center font-bold" />
      </div>
      <button type="button" disabled={!opp || busy || stake < dc.min_stake || stake > dc.max_stake || (user.points ?? 0) < stake} onClick={send} data-testid="duel-send-btn"
        className="w-full h-12 rounded-full pink-gradient text-white font-extrabold inline-flex items-center justify-center gap-2 disabled:opacity-50 active:scale-95 transition-transform">
        {busy ? <Loader2 className="w-5 h-5 animate-spin" /> : <Swords className="w-5 h-5" />} Herausfordern · {stake} Punkte
      </button>
    </div>
  );
}

const RES_KEY = "itz_seen_duel_results";
function useResultFx(d, ref) {
  useEffect(() => {
    if (d.status !== "done") return;
    let seen = [];
    try { seen = JSON.parse(localStorage.getItem(RES_KEY) || "[]"); } catch { /* ignore */ }
    if (seen.includes(d.id)) return;
    localStorage.setItem(RES_KEY, JSON.stringify([...seen, d.id].slice(-50)));
    const t = setTimeout(() => {
      const r = ref.current?.getBoundingClientRect();
      if (d.won) { playFanfare(); confetti({ x: r ? r.left + r.width / 2 : innerWidth / 2, y: r ? r.top + 30 : innerHeight / 2, count: 110, spread: 1.5 }); }
      else if (!d.tie) playLose();
    }, 400);
    return () => clearTimeout(t);
  }, [d.id, d.status]); // eslint-disable-line react-hooks/exhaustive-deps
}

function DuelRow({ d, reload, openWith, onVersus }) {
  const { user, refreshUser } = useAuth();
  const ref = useRef(null);
  useResultFx(d, ref);
  const act = (path, ok) => api.post(`/duels/${d.id}/${path}`).then(() => {
    toast.success(ok); refreshUser(); reload();
    if (path === "accept") onVersus({ players: [d.other, user], title: "Duell gestartet", text: `24 Stunden, ${d.stake * 2} Punkte im Pott – wer sammelt mehr?`, cta: "Viel Glück! 🍀" });
  }).catch((e) => toast.error(errMsg(e)));
  const lead = d.my_score - d.other_score;
  return (
    <li ref={ref} className={`rounded-2xl border bg-white p-4 space-y-3 fade-up ${d.status === "active" ? "border-violet-200 shadow-sm shadow-violet-100" : "border-pink-100"}`} data-testid={`duel-${d.id}`}>
      <div className="flex items-center gap-3">
        <UserAvatar userId={d.other.user_id} name={d.other.name} picture={d.other.picture} size="w-10 h-10" text="text-sm" />
        <div className="min-w-0 flex-1">
          <p className="font-bold truncate">{d.other.name}</p>
          <p className="text-xs text-slate-500">Einsatz {d.stake} · Gewinn {d.stake * 2} Punkte</p>
          {(d.cheers_me > 0 || d.cheers_other > 0) && <p className="text-[11px] font-bold text-rose-500" data-testid={`duel-cheers-${d.id}`}>❤️ {d.cheers_me} {d.cheers_me === 1 ? "Fan feuert" : "Fans feuern"} dich an · {d.cheers_other} für {d.other.name.split(" ")[0]}</p>}
        </div>
        {d.status === "active" && <span className="text-[11px] font-bold text-slate-400 inline-flex items-center gap-1"><Clock className="w-3.5 h-3.5" />{left(d.ends_at)}</span>}
        {d.status === "done" && <span data-testid={`duel-result-${d.id}`} className={`text-xs font-extrabold px-2.5 py-1 rounded-full ${d.won ? "bg-emerald-100 text-emerald-700" : d.tie ? "bg-slate-100 text-slate-600" : "bg-rose-100 text-rose-600"}`}>{d.won ? <><span className="trophy-bounce">🏆</span> +{d.stake * 2}</> : d.tie ? "Unentschieden" : "Verloren"}</span>}
      </div>
      {d.status !== "pending" && (
        <div className="grid grid-cols-2 gap-2 text-center">
          <div className={`rounded-xl p-2 ${lead >= 0 ? "bg-emerald-50" : "bg-slate-50"}`}><p className="text-[11px] font-bold text-slate-500">Du</p><p className="font-display text-2xl font-black" data-testid={`duel-my-score-${d.id}`}>{d.my_score}</p>{d.status === "active" && lead > 0 && <p className="text-[10px] font-extrabold text-emerald-600">Du führst! 🔥</p>}</div>
          <div className={`rounded-xl p-2 ${lead < 0 ? "bg-emerald-50" : "bg-slate-50"}`}><p className="text-[11px] font-bold text-slate-500 truncate">{d.other.name.split(" ")[0]}</p><p className="font-display text-2xl font-black">{d.other_score}</p></div>
        </div>
      )}
      {d.status === "done" && !d.won && !d.tie && !openWith?.has(d.other.user_id) && (
        <button type="button" onClick={(e) => { const el = e.currentTarget; api.post("/duels", { opponent_id: d.other.user_id, stake: d.stake }).then(() => { successPop(el); toast.success(`Revanche gefordert – ${d.stake} Punkte Einsatz ⚔️`); refreshUser(); reload(); }).catch((er) => toast.error(errMsg(er))); }}
          data-testid={`duel-rematch-${d.id}`} className="w-full h-11 rounded-full bg-gradient-to-r from-violet-500 to-pink-500 text-white font-extrabold inline-flex items-center justify-center gap-2 active:scale-95 transition-transform">
          <Swords className="w-4 h-4" /> Revanche! · {d.stake} Punkte
        </button>
      )}
      {d.status === "pending" && (d.incoming ? (
        <div className="flex gap-2">
          <button type="button" onClick={() => act("accept", "Duell angenommen – 24 Stunden, los geht's! ⚔️")} data-testid={`duel-accept-${d.id}`} className="flex-1 h-11 rounded-full pink-gradient text-white font-extrabold inline-flex items-center justify-center gap-1.5"><Check className="w-4 h-4" /> Annehmen ({d.stake})</button>
          <button type="button" onClick={() => act("decline", "Duell abgelehnt")} data-testid={`duel-decline-${d.id}`} className="h-11 px-4 rounded-full bg-slate-100 font-bold text-slate-600 inline-flex items-center gap-1"><X className="w-4 h-4" /> Ablehnen</button>
        </div>
      ) : (
        <div className="flex items-center justify-between gap-2 text-sm text-slate-500">
          <span>Wartet auf Antwort · {left(d.expires_at)}</span>
          <button type="button" onClick={() => act("decline", "Herausforderung zurückgezogen – Einsatz zurück")} data-testid={`duel-cancel-${d.id}`} className="font-bold text-rose-600">Zurückziehen</button>
        </div>
      ))}
    </li>
  );
}

export function DuelsSection() {
  const [list, setList] = useState(null);
  const [vs, setVs] = useState(null);
  const load = useCallback(() => api.get("/duels").then(({ data }) => setList(data)).catch(() => setList([])), []);
  useEffect(() => { load(); const iv = setInterval(load, 60000); return () => clearInterval(iv); }, [load]);
  const open = (list || []).filter((d) => d.status !== "done"), done = (list || []).filter((d) => d.status === "done");
  return (
    <section className="rounded-[2rem] bg-white border border-pink-100 p-5 sm:p-7 shadow-sm space-y-5" data-testid="duels-section">
      <div className="flex items-start gap-3">
        <span className="w-12 h-12 shrink-0 rounded-2xl bg-gradient-to-br from-violet-500 to-pink-500 flex items-center justify-center"><Swords className="w-6 h-6 text-white" /></span>
        <div>
          <h2 className="font-display text-xl font-extrabold">Freundes-Duelle</h2>
          <p className="text-sm text-slate-500">Beide setzen Punkte ein. Wer in 24 Stunden mehr Punkte sammelt, gewinnt alles.</p>
        </div>
      </div>
      <NewDuel onCreated={load} onVersus={setVs} />
      {vs && <DuelVersus {...vs} onClose={() => setVs(null)} />}
      {list === null ? <Loader2 className="w-6 h-6 animate-spin text-rose-400 mx-auto" /> : <>
        {open.length > 0 && <ul className="space-y-3" data-testid="duels-open">{open.map((d) => <DuelRow key={d.id} d={d} reload={load} onVersus={setVs} />)}</ul>}
        {done.length > 0 && <div className="space-y-3"><p className="text-xs font-bold uppercase tracking-widest text-slate-400 inline-flex items-center gap-1"><Trophy className="w-3.5 h-3.5" /> Beendet</p><ul className="space-y-3" data-testid="duels-done">{done.map((d) => <DuelRow key={d.id} d={d} reload={load} onVersus={setVs} openWith={new Set(open.map((o) => o.other.user_id))} />)}</ul></div>}
      </>}
      <p className="text-xs text-slate-400">Zählt: alle Punkte aus Login, Glücksrad, Beiträgen, Missionen & Co. Nicht gezählt: Rubbellose, Admin-Gutschriften und Community-Bonus. Nicht angenommene Duelle verfallen nach 24 Std., der Einsatz kommt zurück. Bei Gleichstand bekommt jeder seinen Einsatz zurück.</p>
    </section>
  );
}
